import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactElement,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IFilterInfo } from 'redux/reducers/usage/filter/filter';
import {
  selectStreamingCustomers,
  selectDownloadCustomers,
  loadCustomers,
} from 'redux/reducers/customers';

import sortByRecent from './utils/sortByRecent';
import addRecent from 'helpers/addRecent';
import addLabel from 'helpers/addLabel';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import { TopicFilterNames, ITopicFilterState } from './shared';
import { Button, ButtonVariant } from './TopicFilters.styles';
import styled from 'styled-components/macro';
import Box from 'components/elements/Box';
import TopicFilterCustomerDropdown from 'components/base/TopicFiltersDropdown/CustomerDropdown';
import { resetActuality } from '../../redux/reducers/actuality';

const ListLabelSpan = styled('span')`
  display: block;
  margin-left: 12px;
  font-size: 12px;
  line-height: 41px;
  letter-spacing: 0.5px;
  color: #8ea6be;
`;

interface IDSPFilterContainerProps {
  setFilter: any;
  clearFilterUrl: any;
  recentFilters: IFilterInfo[];
  filters: ITopicFilterState;
  isSmallScreen: boolean;
  isStreamingAvailable?: boolean;
  isDownloadsAvailable?: boolean;
}

const DSPFilterContainer: React.FC<IDSPFilterContainerProps> = (props) => {
  const {
    setFilter,
    clearFilterUrl,
    recentFilters,
    filters,
    isStreamingAvailable,
    isDownloadsAvailable,
  } = props;

  const [isStreaming, setIsStreaming] = useState(isStreamingAvailable);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (filters.download) {
      clearFilterUrl('streaming');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.download]);

  useEffect(() => {
    if (filters.streaming) {
      clearFilterUrl('download');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.streaming]);

  useEffect(() => {
    if (isStreamingAvailable) {
      setIsStreaming(true);
    } else if (isDownloadsAvailable) {
      setIsStreaming(false);
    }
  }, [isStreamingAvailable, isDownloadsAvailable]);

  const getRecentFiltersFor = useCallback(
    (filterName: TopicFilterNames): IFilterInfo[] => {
      const result = recentFilters.filter(
        (recent: any) => recent.filterType === filterName
      );
      result.splice(3);
      return result;
    },
    [recentFilters]
  );

  const streamingCustomers = useSelector(selectStreamingCustomers);
  const sortedStreamingCustomers = useMemo(() => {
    const sorted = streamingCustomers
      .map(addLabel)
      .map(addRecent(getRecentFiltersFor('streaming')))
      .sort(sortByRecent);
    return [...sorted];
  }, [streamingCustomers, getRecentFiltersFor, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCustomers = useSelector(selectDownloadCustomers);
  const sortedDownloadCustomers = useMemo(() => {
    const sorted = downloadCustomers
      .map(addLabel)
      .map(addRecent(getRecentFiltersFor('download')))
      .sort(sortByRecent);
    return [...sorted];
  }, [downloadCustomers, getRecentFiltersFor, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCustomerStreamingFilter = useMemo(
    () =>
      sortedStreamingCustomers.find(
        (customer: any): boolean => customer.id === filters.streaming
      ),
    [sortedStreamingCustomers, filters.streaming]
  );

  const selectedCustomerDownloadFilter = useMemo(
    () =>
      sortedDownloadCustomers.find(
        (customer: any): boolean => customer.id === filters.download
      ),
    [sortedDownloadCustomers, filters.download]
  );

  const items = isStreaming
    ? sortedStreamingCustomers
    : sortedDownloadCustomers;
  // const selectedItem =
  //   selectedCustomerStreamingFilter || selectedCustomerDownloadFilter;
  const selectedItem = isStreaming
    ? selectedCustomerStreamingFilter ||
      (isDownloadsAvailable && selectedCustomerDownloadFilter)
    : selectedCustomerDownloadFilter;

  const onChange = (item: any): void => {
    dispatch(resetActuality());
    setFilter(isStreaming ? 'streaming' : 'download', item?.id);
    logToAmplitude('Filter has been updated', {
      type: 'DSP',
      value: item?.displayName,
    });
  };

  const ListLabel = (): ReactElement => (
    <ListLabelSpan>
      {`SELECT ${isStreaming ? 'STREAMING' : 'DOWNLOAD'} DSP`}
    </ListLabelSpan>
  );

  const clearFilter = useCallback((): void => {
    if (filters.download || filters.streaming) {
      dispatch(resetActuality());
      clearFilterUrl(
        selectedCustomerStreamingFilter ? 'streaming' : 'download'
      );
      logToAmplitude('Filter has been updated', { type: 'DSP' });
    }
  }, [selectedCustomerStreamingFilter, clearFilterUrl]);

  const onClickOutside = (): void => setIsStreaming(isStreamingAvailable);

  const clearFilterOutside = (): void => {
    clearFilter();
    setIsStreaming(true);
  };

  return (
    <TopicFilterCustomerDropdown
      onChange={onChange}
      items={items}
      selectedItem={selectedItem}
      defaultLabel="All"
      ListLabel={ListLabel}
      clearFilter={clearFilterOutside}
      onClickOutsideCustom={onClickOutside}
    >
      <Box p={4} px={12} height={'98px'} borderTop={'1px solid #E8EBED'}>
        <ButtonVariant
          margin="0 0 6px 0"
          onClick={(): void => setIsStreaming(!isStreaming)}
          disabled={isStreaming ? !isDownloadsAvailable : !isStreamingAvailable}
        >
          <span>{isStreaming ? 'DOWNLOAD' : 'STREAMING'} DSPS</span>
        </ButtonVariant>
        <Button
          borderColor={'LightGrey'}
          borderWidth={'1px'}
          borderStyle={'solid'}
          fontSize={14}
          py={'3.8px'}
          isActive={!!selectedItem}
          onClick={clearFilter}
          isClearButton
        >
          <IconClearFilter />
          Clear this filter
        </Button>
      </Box>
    </TopicFilterCustomerDropdown>
  );
};

export default DSPFilterContainer;
